import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Layers, Handshake, ShieldCheck } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const VALUES = [
  { icon: Target, title: "Business First", desc: "Every website and AI agent is designed around one goal — helping your business win and serve more customers." },
  { icon: Layers, title: "Practical Technology", desc: "We recommend only the website features, AI agents, and integrations that genuinely fit your requirements and budget." },
  { icon: Handshake, title: "Clear Commitments", desc: "Clear project scope, transparent pricing, and defined support agreements — no surprises after launch." },
  { icon: ShieldCheck, title: "Responsible AI", desc: "Our AI agents use supported platform integrations, respect messaging policies, and hand over to humans when needed." },
];

export default function About() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "About Us | RIDGELINE DIGI PARTNER";
  }, []);

  return (
    <div className="pt-[84px]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <SectionHead eyebrow="ABOUT US" title="Your Business. Digitally Powered. Intelligently Automated."
            description="RIDGELINE DIGI PARTNER is a technology solutions provider helping businesses build a stronger digital presence and automate customer communication. We combine professional website development with AI voice agents and intelligent AI agents for WhatsApp, websites, Instagram, and Facebook — so no customer enquiry ever goes unanswered." />
          <Reveal>
            <div className="glass-card rounded-2xl p-8 sm:p-10">
              <h3 className="font-display text-xl font-semibold text-slate-50">What We Do</h3>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
                We serve manufacturers, hospitals and clinics, retailers, educational institutions, real estate
                companies, consultants, recruiters, logistics businesses, and growing enterprises of every kind.
                Our work covers the full customer journey: the website that earns trust, the AI voice agent that
                answers every call, and the messaging agents that respond on WhatsApp, your website, Instagram,
                and Facebook — all feeding one organized enquiry pipeline for your sales team.
              </p>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
                From the first consultation to launch and ongoing support, we work as your digital partner —
                understanding your business, designing the right solution, building it carefully, and standing
                behind it after go-live.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <Marquee />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <SectionHead eyebrow="HOW WE WORK" title="The Principles Behind Every Project" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="glass-card rounded-2xl p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:border-blue-500/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-400">
                  <v.icon size={20} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14 text-center">
          <button onClick={() => navigate("/contact")}
            className="rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(37,99,235,0.4)] transition-all hover:bg-blue-500 hover:-translate-y-0.5">
            Get a Free Consultation
          </button>
        </Reveal>
      </section>
    </div>
  );
}
